"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Heart } from "lucide-react";
import { addToFav, removeFromFav } from "@/services/fav.service";
import { useToken } from "@/lib/use-token";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

const AddToFav = ({
  isFav,
  productId,
  isDetails = false,
}: {
  isFav: boolean;
  productId: number;
  isDetails?: boolean;
}) => {
  const t = useTranslations();
  const { hasToken } = useToken();
  const router = useRouter();
  const locale = useLocale();

  const [isFavorite, setIsFavorite] = useState(isFav);
  const [isPending, setIsPending] = useState(false);

  const prevIsFavPropRef = useRef(isFav);
  useEffect(() => {
    if (prevIsFavPropRef.current !== isFav) {
      prevIsFavPropRef.current = isFav;
      setIsFavorite(isFav);
    }
  }, [isFav]);

  const toggleFav = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (!isDetails) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!hasToken()) {
      toast(t("youShouldLoginFirst"));
      router.push("/login");
      return;
    }
    if (isPending) return;

    const newFav = !isFavorite;
    setIsFavorite(newFav);
    setIsPending(true);
    try {
      const res = newFav
        ? await addToFav(productId)
        : await removeFromFav(productId);
      if (res.status !== 200) {
        setIsFavorite(!newFav);
      } else {
        toast.success(newFav ? t("addedToFav") : t("removedFromFav"));
      }
    } catch {
      setIsFavorite(!newFav);
    } finally {
      setIsPending(false);
    }
  };

  return isDetails ? (
    <Button
      onClick={toggleFav}
      disabled={isPending}
      className="bg-secondary/20 hover:bg-secondary/30 group"
    >
      <Heart
        className={
          isFavorite
            ? "text-secondary fill-secondary transition-colors"
            : "text-black transition-colors"
        }
      />
    </Button>
  ) : (
    <button
      className={cn(
        "absolute top-2 sm:top-4 z-10 cursor-pointer bg-white rounded-full p-1.5 sm:p-2",
        locale === "ar" ? "left-2 sm:left-4" : "right-2 sm:right-4",
      )}
      onClick={toggleFav}
      disabled={isPending}
    >
      <Heart
        className={cn(
          "size-4 sm:size-5",
          isFavorite
            ? "text-secondary fill-secondary transition-colors"
            : "text-secondary hover:fill-secondary transition-colors",
        )}
      />
    </button>
  );
};

export default AddToFav;
